import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TextsmsIcon from '@mui/icons-material/Textsms';
import { Box, styled } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ReactEventHandler } from 'react';
import { HiCursorClick, HiOfficeBuilding, HiOutlineArchive } from 'react-icons/hi';
import {
  ICandidate,
  ICandidateRevenue,
  IJob,
  ILocation,
  Interaction,
  amplitude,
  candiateDefaultFieldMapping,
  fetchCandiateFieldMapping,
  getSubmissionSettings,
} from 'wisestep-api';
import {
  WsIconLocation,
  WsIconTime,
  WsIconPosition,
  WsIconLikeLocation,
  WsIconProfile,
  WsIconDice,
  WsColors,
  IWorkspaceContext,
  formatDateToMonthDayYearString,
  WsIconBoard,
  WsIconCurrency,
  WsIconVisa,
  timeSince,
} from 'wisestep-base';

import { getRawPhoneNumber } from '../../../features/conversationWidget/utils/fieldDefaultValues';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { CandidateDetails } from '../components/childComponets/CandidateDetailsCard/CandidateDetailsCard';

export const apiFailedMessage =
  'There was an error in processing your search request. We have logged the error and our team will review it.Please try again with different search criteria';
export const LayoutTheme = styled(Box)(() => ({
  maxWidth: '100%',
  maxHeight: 'xs',
  backgroundColor: WsColors.TertiaryShades50,
}));

export const AppLayoutTheme = styled(Box)(() => ({
  borderRadius: 8,
  backgroundColor: WsColors.TheBlues900,
}));

export enum CandidateType {
  SUBMISSIONS = 'submissions',
  TALENTPOOL = 'talentpool',
  PIPELINE = 'pipeline',
  SMART_SOURCE = 'smartSource',
}

export const copySuccess = () => success('Copied Successfully !!');
export const copyFailed = () => failed('Copy failed !! ');
export const copyWarn = () => warning('Clipboard not supported');

export const getCandidateDetails = (
  candidate: ICandidate,
  type: CandidateType
): CandidateDetails[] => {
  if (
    (candidate.source === 'LOCAL' ||
      candidate.source === 'CVUPLOAD_LOCAL' ||
      candidate.source === 'GDRIVE' ||
      candidate.source === 'CVUPLOAD_GDRIVE' ||
      candidate?.candidateStatusTags?.includes('applied')) &&
    (type === CandidateType.TALENTPOOL || type === CandidateType.PIPELINE)
  ) {
    return getParsedCandidateDetails(candidate, type);
  }
  const array: CandidateDetails[] = [];
  if (candidate.currentLocation) {
    array.push({
      icon: <WsIconLocation />,
      value: getLocation(candidate.currentLocation) ?? 'NA',
      title: 'Candidate Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      value: `NA`,
      title: 'Candidate Location',
    });
  }
  if (
    !isEmpty(candidate.companyName) &&
    (type === CandidateType.SUBMISSIONS || type === CandidateType.PIPELINE)
  ) {
    array.push({
      icon: <HiOfficeBuilding color={WsColors.TertiaryShades700} />,
      value: candidate.companyName ?? 'NA',
      title: 'Client name',
    });
  }
  if (candidate.currentJobTitle) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.currentEmployer
        ? `${candidate.currentJobTitle} @ ${candidate.currentEmployer}`
        : candidate.currentJobTitle,
      title: 'Latest JobTitle',
    });
  } else if (candidate.previousJobTitles?.[0]) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.previousJobTitles?.[0],
      title: 'Latest JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      value: 'NA',
      title: 'Latest JobTitle',
    });
  }
  if (candidate?.totalYearsExperience || candidate?.totalMonthsExperience) {
    array.push({
      icon: <WsIconPosition />,
      value:
        candidate?.totalYearsExperience && candidate.totalYearsExperience > 1
          ? `${candidate.totalYearsExperience}.${
              candidate?.totalMonthsExperience ? candidate?.totalMonthsExperience % 12 : 0
            } years Exp`
          : `${candidate.totalYearsExperience ?? 0}.${
              candidate?.totalMonthsExperience ? candidate?.totalMonthsExperience % 12 : 0
            } year Exp`,
      title: "Candidate's Experience",
    });
  } else {
    array.push({
      icon: <WsIconPosition />,
      value: 'NA',
      title: "Candidate's Experience",
    });
  }
  array.push({
    icon: <WsIconDice />,
    value:
      type === CandidateType.TALENTPOOL
        ? candidate.sources?.[0] ?? candidate.source ?? `NA`
        : candidate.source ?? `NA`,
    title: 'Source',
  });

  if (candidate.jobPreferencePermanent || candidate.jobPreferenceContract) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `Prefers ${candidate.jobPreferenceContract ? 'Contract' : 'Permanent'} `,
      title: 'Preferred Job Type',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Preferred Job Type',
    });
  }

  array.push({
    icon: <WsIconLikeLocation color={WsColors.TertiaryShades700} />,
    value: getPreferredLocation(candidate),
    title: 'Preferred Locations',
  });

  array.push({
    icon: <WsIconLikeLocation color={WsColors.TertiaryShades700} />,
    value: candidate?.relocationPreference ?? 'NA',
    title: 'Relocation Preferences',
  });

  array.push({
    icon: <WsIconCurrency color={WsColors.TertiaryShades700} />,
    value:
      candidate.expectedSalaryCurrency &&
      candidate.expectedBaseSalaryTimeSpan &&
      candidate.expectedBaseSalary
        ? `${candidate.expectedSalaryCurrency} ${String(candidate.expectedBaseSalary)} ${
            candidate.expectedBaseSalaryTimeSpan ?? ''
          }`
        : 'NA',
    title: 'Expected Salary',
  });

  array.push({
    icon: <WsIconCurrency color={WsColors.TertiaryShades700} />,
    value:
      candidate.currentSalaryCurrency && candidate.currentSalaryTimeSpan && candidate.currentSalary
        ? `${candidate.currentSalaryCurrency} ${String(candidate.currentSalary)} 
        ${candidate.currentSalaryTimeSpan}`
        : 'NA',
    title: 'Current Salary',
  });

  if (candidate?.appliedOn) {
    array.push({
      icon: <HiCursorClick color={WsColors.TertiaryShades700} />,
      value: (candidate?.appliedOn && timeSince(new Date(candidate?.appliedOn).toString())) ?? 'NA',
      title: 'Applied On',
    });
  }

  array.push({
    icon: <WsIconVisa />,
    value: `${
      candidate.workAuthorization && candidate.workAuthorization?.length > 0
        ? candidate.workAuthorization
        : candidate.workAuthorizationCategory && candidate.workAuthorizationCategory.length > 0
        ? candidate.workAuthorizationCategory
        : 'NA'
    }`,
    title: 'Visa Details',
  });

  if (candidate.noticePeriodInDays) {
    array.push({
      icon: <WsIconBoard />,
      value: `${candidate.noticePeriodInDays} Days`,
      title: 'Notice period',
    });
  } else {
    array.push({
      icon: <WsIconBoard />,
      value: `NA`,
      title: 'Notice period',
    });
  }
  if (!isEmpty(candidate.jobClientRecruiterName) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.jobClientRecruiterName ?? 'NA',
      title: 'Client SPOC',
    });
  }
  if (!isEmpty(candidate.jobAccountManagerName) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.jobAccountManagerName ?? `NA`,
      title: 'Account manager',
    });
  }
  if (!isEmpty(candidate.jobLocations) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconLocation />,
      value: getLocation(candidate?.jobLocations?.[0] as ILocation) ?? `NA`,
      title: 'Job Location',
    });
  }
  return array;
};

const getParsedCandidateDetails = (
  candidate: ICandidate,
  type: CandidateType
): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  if (candidate.currentLocation) {
    array.push({
      icon: <WsIconLocation />,
      value: getLocation(candidate.currentLocation) ?? 'NA',
      title: 'Candidate Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      value: `NA`,
      title: 'Candidate Location',
    });
  }

  if (candidate.currentJobTitle) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.currentEmployer
        ? `${candidate.currentJobTitle} @ ${candidate.currentEmployer}`
        : candidate.currentJobTitle,
      title: 'Latest JobTitle',
    });
  } else if (candidate.previousJobTitles?.[0]) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.previousJobTitles?.[0],
      title: 'Latest JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      value: 'NA',
      title: 'Latest JobTitle',
    });
  }
  if (candidate?.totalYearsExperience) {
    array.push({
      icon: <WsIconTime />,
      value:
        candidate?.totalYearsExperience > 1
          ? `${candidate.totalYearsExperience} years Exp`
          : `${candidate.totalYearsExperience} year Exp`,
      title: "Candidate's Experience",
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      value: `NA`,
      title: "Candidate's Experience",
    });
  }
  array.push({
    icon: <WsIconDice />,
    value:
      type === CandidateType.TALENTPOOL
        ? candidate.sources?.[0] ?? candidate.source ?? `NA`
        : candidate.source ?? `NA`,
    title: 'Source',
  });

  if (candidate.educationQualifications && candidate.educationQualifications?.length > 0) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `${candidate?.educationQualifications[0]} `,
      title: 'Education Qualifications',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Education Qualifications',
    });
  }

  if (candidate.education) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `${candidate?.education[0]?.org} `,
      title: 'Education Inst',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Education Inst',
    });
  }

  if (candidate.previousJobTitles && candidate.previousJobTitles?.length > 0) {
    array.push({
      icon: <WsIconPosition color={WsColors.TertiaryShades700} />,
      value: candidate.previousJobTitles[0],
      title: 'Previous JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconPosition color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Previous JobTitle',
    });
  }
  if (candidate.currentEmployer) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.currentEmployer,
      title: 'Current Employer',
    });
  } else {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Current Employer',
    });
  }

  if (candidate.gender === 'FEMALE' || candidate.gender === 'MALE') {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.gender,
      title: 'Gender',
    });
  }
  if (candidate.dob) {
    array.push({
      icon: <WsIconTime color={WsColors.TertiaryShades700} />,
      value: formatDateToMonthDayYearString(new Date(candidate?.dob)),
      title: 'DOB',
    });
  }
  if (candidate.candidateLanguages && candidate?.candidateLanguages?.length > 0) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.candidateLanguages?.map((language) => language.languageName).toString(),
      title: 'Languages Know',
    });
  }

  return array;
};

export const getPersonalDetails = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.visaStatus) && candidate.visaStatus ? candidate.visaStatus : 'NA',
    title: 'Visa Status',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.visaValidity) && candidate.visaValidity ? candidate.visaValidity : 'NA',
    title: 'Visa Validity',
  });
  array.push({
    icon: <WsIconVisa />,
    value: `${
      candidate.workAuthorization && candidate.workAuthorization?.length > 0
        ? candidate.workAuthorization
        : candidate.workAuthorizationCategory && candidate.workAuthorizationCategory.length > 0
        ? candidate.workAuthorizationCategory
        : 'NA'
    }`,
    title: 'Authorization',
  });
  candidate?.alternateEmails?.length > 0 &&
    array.push({
      icon: <WsIconVisa />,
      value: candidate?.alternateEmails?.length > 0 ? candidate.alternateEmails : 'NA',
      title: 'Alternate Email Address',
      type: 'EMAIL',
    });
  candidate?.alternatePhoneNumbers?.length > 0 &&
    array.push({
      icon: <WsIconVisa />,
      value: candidate?.alternatePhoneNumbers?.length > 0 ? candidate.alternatePhoneNumbers : 'NA',
      title: 'Alternate Mobile Numbers',
      type: 'EMAIL',
    });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.socialProfiles ? candidate.socialProfiles.url : 'NA',
    title: 'Social Link',
    type: 'ICON',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.profileURLs && candidate.profileURLs.length > 0 ? candidate.profileURLs : 'NA',
  //   title: 'Profile Urls',
  // });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.nationality) ? (candidate.nationality as string) : 'NA',
    title: 'Nationality',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.dob ? new Date(candidate.dob).toLocaleDateString() : 'NA',
    title: 'Date of Birth',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.currentLocation && !isEmpty(getLocation(candidate?.currentLocation))
        ? getLocation(candidate?.currentLocation)
        : 'NA',
    title: 'Current Location',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.gender) && candidate.gender ? candidate.gender : 'NA',
    title: 'Gender',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: 'NA',
  //   title: 'Messenger ID',
  // });
  return array;
};

export const getProfessionalDetails = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.currentEmployer) && candidate.currentEmployer
        ? candidate.currentEmployer
        : 'NA',
    title: 'Latest Employer',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.currentJobTitle) && candidate.currentJobTitle
        ? candidate.currentJobTitle
        : 'NA',
    title: 'Latest Job Title',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.totalYearsExperience ? `${candidate.totalYearsExperience}` : 'NA',
    title: 'Total experience',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.previousEmployers) && candidate.previousEmployers
        ? candidate.previousEmployers
        : 'NA',
    title: 'Previous Employers',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.previousJobTitles) && candidate.previousJobTitles
        ? candidate.previousJobTitles
        : 'NA',
    title: 'Previous Job Titles',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.relevantYearsExperience ? `${candidate.relevantYearsExperience}` : 'NA',
    title: 'Relevant experience',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value:
  //     !isEmpty(candidate.certifications) && candidate.certifications
  //       ? candidate.certifications
  //       : 'NA',
  //   title: 'Certifications',
  // });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate?.educationQualifications && candidate?.educationQualifications?.length > 0
        ? candidate?.educationQualifications
        : candidate?.highestEducation
        ? candidate?.highestEducation
        : 'NA',
    title: 'Highest education qualifications',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.institutionsAttended) && candidate.institutionsAttended
        ? candidate.institutionsAttended
        : 'NA',
    title: 'Institutions Attended',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: 'NA',
  //   title: 'Documents',
  // });
  return array;
};
export const getCompenstation = (
  candidate: ICandidate,
  revenue?: ICandidateRevenue | undefined
): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.currentSalary
      ? `${candidate.currentSalaryCurrency} ${candidate.currentSalary} ${candidate.currentSalaryTimeSpan}`
      : 'NA',
    title: 'Current Salary',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.expectedBaseSalary
      ? `${candidate.expectedSalaryCurrency} ${candidate.expectedBaseSalary} ${candidate.expectedBaseSalaryTimeSpan}`
      : 'NA',
    title: 'Expected Salary',
  });
  {
    candidate.jobType === 'Permanent'
      ? array.push({
          icon: <WsIconVisa />,
          value: revenue
            ? `${revenue.clientBillRateCurrency} ${revenue.netFeeIncome} ${revenue.clientBillRateTimeSpan}`
            : 'NA',
          title: 'Net Revenue',
        })
      : array.push({
          icon: <WsIconVisa />,
          value: revenue
            ? `${revenue.clientBillRateCurrency} ${revenue.netMargin?.toString()} ${
                revenue.clientBillRateTimeSpan
              }`
            : 'NA',
          title: 'Potential Net Margin',
        });
  }
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.otherExpectedAllowances) && candidate.otherExpectedAllowances
        ? candidate.otherExpectedAllowances
        : 'NA',
    title: 'Expected allowances and benefits',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.otherCurrentAllowances) && candidate.otherCurrentAllowances
        ? candidate.otherCurrentAllowances
        : 'NA',
    title: 'Other allowances and benefits',
  });
  return array;
};

export const getJobPreferencInfo = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.jobPreferencePermanent && candidate.jobPreferenceContract
        ? 'Contract & Permanent'
        : candidate.jobPreferenceContract
        ? 'Contract '
        : candidate.commPreferencePhone
        ? 'Permanent'
        : 'NA',
    title: 'Job Preference',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.currentJobType ?? 'NA',
    title: 'Latest Job Type',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.willingToRelocate != null ? (candidate.willingToRelocate ? 'YES' : 'NO') : 'NA',
    title: 'Willing to Relocate',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.willingToTravel != null ? (candidate.willingToTravel ? 'YES' : 'NO') : 'NA',
  //   title: 'Willing to Travel',
  // });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.jobIncludesTravel != null ? (candidate.jobIncludesTravel ? 'YES' : 'NO') : 'NA',
    title: 'Latest Job Includes Travel',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.familyWillingToRelocate != null
        ? candidate.familyWillingToRelocate
          ? 'YES'
          : 'NO'
        : 'NA',
    title: 'Family willing to relocate',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.noticePeriod ?? 'NA',
    title: 'Notice Period',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.interviewingWithOthers != null
        ? candidate.interviewingWithOthers
          ? 'YES'
          : 'NO'
        : 'NA',
    title: 'Interviewing With Others',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.currentLocation ? getLocation(candidate.currentLocation) : 'NA',
  //   title: 'Location',
  // });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.availableFrom ?? 'NA',
  //   title: 'Available From',
  // });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.reasonForNewJob) && candidate.reasonForNewJob
        ? candidate.reasonForNewJob
        : 'NA',
    title: 'Reason for looking for a new job',
  });
  return array;
};
export const getOtherInfo = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.commPreferenceEmail && candidate.commPreferencePhone
        ? 'Email & Phone'
        : candidate.commPreferenceEmail
        ? 'Email '
        : candidate.commPreferencePhone
        ? 'Phone'
        : 'NA',
    title: 'Communication Preference',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.sources?.[0] ?? 'NA',
    title: 'Source',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.candidateLanguages) && candidate.candidateLanguages
        ? candidate.candidateLanguages.map((language) => language.languageName)
        : 'NA',
    title: 'Languages Known',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.tags) && candidate.tags ? candidate.tags : 'NA',
    title: 'Tags',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.aadharNumber) && candidate.aadharNumber ? candidate.aadharNumber : 'NA',
    title: 'Aadhar Number',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.panCard) && candidate.panCard ? candidate.panCard : 'NA',
    title: 'PAN Card',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.passportNumber) && candidate.passportNumber
        ? candidate.passportNumber
        : 'NA',
    title: 'Passport Number',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.passportType) && candidate.passportType ? candidate.passportType : 'NA',
    title: 'Passport Type',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.ssnNumber) && candidate.ssnNumber ? candidate.ssnNumber : 'NA',
    title: 'SSN Number',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.candidateId ?? 'NA',
  //   title: 'Candidate ID',
  // });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: !isEmpty(candidate.assessment) && candidate.assessment ? candidate.assessment : 'NA',
  //   title: 'Recruiter Assessment',
  // });
  return array;
};

export interface CandidateCardProps {
  candidate: ICandidate;
  downloadSource?: string | null;
  job?: IJob | null;
  isSelected: boolean;
  checkBoxOnclick?: ReactEventHandler;
  candidateIndex?: number;
  onCVClick?: (args: number) => void;
  setDownloadSource?: (args: string, index: number) => void;
  onActionClick?: (args: number) => void;
  openSingleEmail: (candidate: ICandidate, email: string) => void;
  queryRefresh?: () => void;
  interaction?: Interaction | undefined;
  amplitudeEventHandler?: (eventType: string) => void;
  onOpenShareIcon?: (args: ICandidate) => void;
  isCvViewed?: boolean;
}

export const getLocation = (location: ILocation) =>
  [location.city, location.state, location.country].filter((x) => x).toString();
export const phoneOptions = [
  {
    label: 'Send SMS',
    onClick: () => {},
    icon: <TextsmsIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Edit',
    onClick: () => {},
    icon: <EditIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Copy',
    onClick: () => {},
    icon: <ContentCopyIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Call',
    onClick: () => {},
    icon: (
      <LocalPhoneOutlinedIcon key={0} fontSize="small" sx={{ color: WsColors.TertiaryShades500 }} />
    ),
  },
];

export const emailOptions = [
  {
    label: 'Send Email',
    id: '0',
    onClick: () => {},
    icon: <MailOutlineIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Copy',
    id: '1',
    onClick: () => {},
    icon: <ContentCopyIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Mark as Primary',
    id: '2',
    onClick: () => {},
    icon: <MailOutlineIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
];

export const firingAmplitudeEvents = (
  name: string,
  clickedOnName: string,
  workspace: IWorkspaceContext,
  pageName: string
) =>
  amplitude(name, {
    clickedOn: clickedOnName,
    workspaceName: workspace.name,
    pageName: pageName,
    planType: workspace.planType,
    interfaceType: 'web',
    pageUrl: window.location.href,
  });

export const getCountryByLocation = (locations: ILocation[]) => {
  const countryLocations: ILocation[] = [];
  locations?.length > 0 &&
    locations.forEach((location) => {
      const countryLocation = {
        country: location.country,
        countryCode: location.countryCode,
        city: '',
        continent: '',
        continentCode: '',
        state: '',
        stateCode: '',
        point: location.point,
      };
      countryLocations.push(countryLocation);
    });
  return countryLocations;
};

export const getAllAlternateNumbers = (data: string[]) => {
  if (!isEmpty(data) && data.includes('false')) {
    return data.filter((val) => val != 'false');
  }
  return isEmpty(data) ? [] : data;
};

export const removeDuplicates = (data: string[], value: string) => {
  if (!isEmpty(data)) {
    return data.filter((val) => val != value);
  }
  return isEmpty(data) ? [] : data;
};

export const removeDuplicatesPhNos = (data: string[], value: string) => {
  if (!isEmpty(data)) {
    return getAllAlternateNumbers(data).filter(
      (val) => !getRawPhoneNumber(val).includes(getRawPhoneNumber(value))
    );
  }
  return isEmpty(data) ? [] : data;
};

const getPreferredLocation = (candidate: ICandidate): string => {
  let preferredLocation = '';
  `${
    candidate?.preferredLocations &&
    candidate?.preferredLocations?.map(
      (location) =>
        (preferredLocation =
          preferredLocation +
          `${location.city ?? ''}  ${location.state ?? ''} ${location.country ?? ''}, `)
    )
  }`;
  return preferredLocation ? preferredLocation : 'NA';
};

export const getFieldSettings = async (isMapping: boolean) => {
  if (isMapping) {
    const req = await fetchCandiateFieldMapping();
    if (!isEmpty(req.data)) {
      return req.data;
    } else {
      const resetDefaultSettingsDetails = await candiateDefaultFieldMapping();
      return resetDefaultSettingsDetails.data;
    }
  }
  const req = await getSubmissionSettings();
  return req.data;
};
